/**
 * Disclaimers admin — Phase 1A.
 * Read-only listing + "new version" creation for both `pre_test` and
 * `auto_control` kinds. The seed inserts a v1 of each; admins bump
 * versions here as the wording is refined.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft, Plus, Spinner, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  seqListDisclaimers, seqCreateDisclaimer,
} from "@/lib/sequencingApi";
import { useT } from "@/lib/i18n";

const KINDS = [
  { code: "pre_test",     label: "Pre-test (before fill/loss)" },
  { code: "auto_control", label: "Auto-control (before live mode)" },
];

export default function SequencingDisclaimersPage() {
  const t = useT();
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]       = useState("");
  const [showCreate, setShowCreate] = useState(false);

  async function refresh() {
    setLoading(true); setErr("");
    try {
      const data = await seqListDisclaimers();
      setItems(data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || String(e));
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="seq-disclaimers-page">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3">
        <Link to="/sequencing" data-testid="seq-disclaimers-back"
              className="w-8 h-8 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
          <ArrowLeft size={16} weight="bold" />
        </Link>
        <ShieldCheck size={20} weight="fill" className="text-blue-700 shrink-0" />
        <h1 className="flex-1 text-base md:text-lg font-bold font-['Chivo'] text-slate-900 truncate">
          {t("seq.disclaimers_title", "Disclaimers")}
        </h1>
        <button type="button" onClick={() => setShowCreate(true)} data-testid="seq-disclaimers-create-open"
                className="text-xs font-bold uppercase tracking-wider px-3 h-9 rounded-sm bg-blue-700 text-white hover:bg-blue-800 inline-flex items-center gap-1.5">
          <Plus size={12} weight="bold" /> {t("seq.disclaimers_new", "New version")}
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6">
        {err && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm">{err}</div>}
        {loading ? (
          <div className="py-10 text-center"><Spinner size={20} className="mx-auto animate-spin text-slate-400" /></div>
        ) : (
          <ul className="space-y-2" data-testid="seq-disclaimers-list">
            {items.map(it => (
              <li key={it.id}
                  className={`bg-white border rounded-sm p-3 ${it.is_active ? "border-emerald-300" : "border-slate-200"}`}
                  data-testid={`seq-disclaimers-row-${it.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded-sm">
                    {it.kind}
                  </span>
                  <span className="text-[11px] text-slate-500 font-mono">v{it.version}</span>
                  {it.is_active && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-sm">
                      active
                    </span>
                  )}
                  <span className="ml-auto text-[10px] text-slate-400">{(it.body_md || "").length} chars</span>
                </div>
                <pre className="text-[11px] text-slate-600 whitespace-pre-wrap font-sans line-clamp-4">
                  {(it.body_md || "").slice(0, 280)}{(it.body_md || "").length > 280 && "…"}
                </pre>
              </li>
            ))}
          </ul>
        )}
      </main>

      {showCreate && (
        <CreateDisclaimerModal onClose={() => setShowCreate(false)}
                               onCreated={() => { setShowCreate(false); refresh(); }} />
      )}
    </div>
  );
}

function CreateDisclaimerModal({ onClose, onCreated }) {
  const t = useT();
  const [kind, setKind]   = useState("pre_test");
  const [body, setBody]   = useState("");
  const [busy, setBusy]   = useState(false);

  async function submit(e) {
    e?.preventDefault?.();
    if (body.trim().length < 10) {
      toast.error(t("seq.disclaimer_too_short", "Disclaimer body must be at least 10 characters"));
      return;
    }
    setBusy(true);
    try {
      await seqCreateDisclaimer({ kind, body_md: body });
      toast.success(t("seq.disclaimer_created", "New disclaimer version published"));
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-30 bg-black/50 flex items-end sm:items-center justify-center p-3"
         data-testid="seq-disclaimers-create-modal">
      <form onSubmit={submit} className="bg-white rounded-sm w-full max-w-lg p-5 space-y-3 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-bold font-['Chivo'] text-slate-900 flex-1">{t("seq.disclaimers_new", "New version")}</h2>
          <button type="button" onClick={onClose} data-testid="seq-disclaimers-create-close"
                  className="w-7 h-7 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-400">
            <X size={14} weight="bold" />
          </button>
        </div>

        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Kind</span>
          <select value={kind} onChange={e => setKind(e.target.value)}
                  data-testid="seq-disclaimers-field-kind"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm bg-white focus:border-blue-500 focus:outline-none">
            {KINDS.map(k => <option key={k.code} value={k.code}>{k.label}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Body (Markdown)</span>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={12} required
                    data-testid="seq-disclaimers-field-body"
                    placeholder="## Disclaimer&#10;&#10;Please confirm…"
                    className="w-full px-3 py-2 text-[11px] border border-slate-300 rounded-sm font-mono focus:border-blue-500 focus:outline-none resize-none" />
          <span className="block text-[10px] text-slate-500 mt-1">
            Publishing this version automatically deactivates the previous active one.
          </span>
        </label>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} data-testid="seq-disclaimers-create-cancel"
                  className="text-xs font-bold uppercase tracking-wider px-3 h-9 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50">
            {t("common.cancel", "Cancel")}
          </button>
          <button type="submit" disabled={busy || body.trim().length < 10}
                  data-testid="seq-disclaimers-create-submit"
                  className="text-xs font-bold uppercase tracking-wider px-4 h-9 rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 inline-flex items-center gap-1.5">
            {busy ? <Spinner size={12} className="animate-spin" /> : <Plus size={12} weight="bold" />}
            {t("seq.publish", "Publish")}
          </button>
        </div>
      </form>
    </div>
  );
}
